/**
 * Per-retailer content for /track/:slug landing pages.
 *
 * Keep slugs lowercase and URL-safe — they become canonical URLs that
 * Search Console crawls. Editing copy here is a content change, not a
 * code change — be deliberate about claims to avoid breaching the Fair
 * Trading Act 1986.
 *
 * `policyConfidence` mirrors the same flag on /api/retailers:
 *   - "confirmed":   retailer publishes a clear price-protection window we can name
 *   - "unconfirmed": no public published window; we ask politely on the customer's behalf
 *
 * Each retailer's `searchTerms` array lists the long-tail queries this page
 * is intended to win in Google — useful for future on-page tweaks if a page
 * underperforms in Search Console.
 */
export const RETAILER_LANDINGS = {
  "pb-tech": {
    slug: "pb-tech",
    id: "pb_tech",
    name: "PB Tech",
    h1Suffix: "PB Tech price drops",
    metaTitle: "PB Tech price drop refunds · PriceGuard NZ",
    metaDescription:
      "If your PB Tech purchase goes on sale within 30 days, PriceGuard NZ helps you claim the difference back under PBCare. Free to use during launch.",
    policyDays: 30,
    policyConfidence: "confirmed",
    policyName: "PBCare",
    policyUrl: "https://www.pbtech.com/nz/about-pbcare",
    storefrontPalette: { from: "#0a0a0a", accent: "#fdb913" }, // PB Tech yellow accent
    summary:
      "PB Tech publishes a 30-day price-protection window under their PBCare programme. If the price on the same product drops within 30 days of you buying it, PB Tech will refund the difference. PriceGuard automates the watching, does the maths, and drafts the email for you.",
    bullets: [
      {
        title: "Six weeks of automatic price-watching",
        body: "From the moment you log a PB Tech purchase, we re-check the PB Tech price every single day for six weeks - well past PBCare's 30-day claim window so we still catch second-wave drops.",
      },
      {
        title: "PBCare-anchored claim email",
        body: "When a drop is spotted inside PBCare's 30-day window, we draft a polite request that cites PB Tech's published PBCare policy by name - you review and send.",
      },
      {
        title: "Receipt + 1-year warranty stored too",
        body: "Drop your PB Tech receipt into the vault and we'll remind you a month before the manufacturer warranty runs out. No spreadsheets.",
      },
    ],
    searchTerms: [
      "pb tech price drop refund",
      "pbcare price protection",
      "pb tech price match after purchase",
    ],
  },
  "jb-hifi": {
    slug: "jb-hifi",
    id: "jb_hifi",
    name: "JB Hi-Fi",
    h1Suffix: "JB Hi-Fi price drops",
    metaTitle: "JB Hi-Fi NZ price drop refunds · PriceGuard NZ",
    metaDescription:
      "JB Hi-Fi NZ publish a competitor price-match policy. PriceGuard watches your purchase daily and drafts a polite refund request the moment a drop is detected. Free during launch.",
    policyDays: 14,
    policyConfidence: "unconfirmed",
    policyName: null,
    policyUrl: "https://www.jbhifi.co.nz/pages/help-and-support/price-match-policy",
    storefrontPalette: { from: "#0a0a0a", accent: "#ffd200" },
    summary:
      "JB Hi-Fi NZ publish a competitor price-match policy for items they currently stock, but don't confirm a specific post-purchase refund window online. Many shoppers report success requesting an adjustment within the first two weeks of purchase. PriceGuard watches every day and writes the polite ask — the call is the retailer's.",
    bullets: [
      {
        title: "Six weeks of daily watching",
        body: "We re-check the JB Hi-Fi price every day for six weeks after purchase. JB Hi-Fi's price-match policy is most often honoured in the first two weeks, but we watch longer to catch the second-wave drops that can land 3-5 weeks later.",
      },
      {
        title: "Honest, retailer-friendly email",
        body: "Our draft asks politely whether the difference can be honoured under JB Hi-Fi's existing price-match or goodwill policy. No demands.",
      },
      {
        title: "Works alongside their price-match policy",
        body: "JB Hi-Fi already price-match before purchase. PriceGuard handles the after-purchase angle nothing else watches for you.",
      },
    ],
    searchTerms: [
      "jb hi-fi nz price drop after purchase",
      "jb hifi price match policy",
      "jb hifi refund difference",
    ],
  },
  "harvey-norman": {
    slug: "harvey-norman",
    id: "harvey_norman",
    name: "Harvey Norman",
    h1Suffix: "Harvey Norman price drops",
    metaTitle: "Harvey Norman NZ price drop refunds · PriceGuard NZ",
    metaDescription:
      "Bought from Harvey Norman NZ? PriceGuard watches the price daily and drafts a polite refund request if it drops shortly after purchase. Free during launch.",
    policyDays: 7,
    policyConfidence: "unconfirmed",
    policyName: null,
    policyUrl: "https://www.harveynorman.co.nz/corporate-information/returns-policy.html",
    storefrontPalette: { from: "#0a0a0a", accent: "#e80a2e" },
    summary:
      "Harvey Norman NZ don't publish a specific post-purchase price-protection window online. Their AU sister site offers 7 days, which we use as a conservative default. PriceGuard watches every day and drafts a polite request — Harvey Norman make the final call.",
    bullets: [
      {
        title: "Six weeks of daily watching",
        body: "We re-check the Harvey Norman price every day for six weeks after purchase. Harvey Norman NZ's claim window isn't publicly stated (their AU sister site uses 7 days); we watch longer so a second-wave drop doesn't get missed.",
      },
      {
        title: "Polite, accurate email draft",
        body: "We never cite a guaranteed refund window Harvey Norman haven't published. We ask respectfully whether the difference can be honoured.",
      },
      {
        title: "Receipt vault for warranty too",
        body: "Big appliances? Drop the receipt in the vault and we'll remind you well before the 2-year typical warranty winds down.",
      },
    ],
    searchTerms: [
      "harvey norman nz price drop after purchase",
      "harvey norman refund difference",
      "harvey norman price match nz",
    ],
  },
};

export const RETAILER_SLUGS = Object.keys(RETAILER_LANDINGS);
